/**
 * Evaluation Cycles Tab
 *
 * Manage evaluation cycles with deadlines.
 */

import { useState } from 'react';
import { useEvaluationCycles, useCreateEvaluationCycle, useUpdateEvaluationCycle, useDeleteEvaluationCycle, useActivateEvaluationCycle } from '../useSettings';
import type { EvaluationCycleDTO, CreateEvaluationCycleRequest, UpdateEvaluationCycleRequest } from '@functieloonhuis/shared';

interface CycleFormData {
  name: string;
  year: number;
  startDate: string;
  endDate: string;
  selfEvaluationDeadline: string;
  managerDeadline: string;
  hrReviewDeadline: string;
}

const currentYear = new Date().getFullYear();

export function EvaluationCyclesTab() {
  const { data: cycles, isLoading, error } = useEvaluationCycles();
  const createCycle = useCreateEvaluationCycle();
  const updateCycle = useUpdateEvaluationCycle();
  const deleteCycle = useDeleteEvaluationCycle();
  const activateCycle = useActivateEvaluationCycle();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<EvaluationCycleDTO | null>(null);
  const [formData, setFormData] = useState<CycleFormData>({
    name: '',
    year: currentYear,
    startDate: '',
    endDate: '',
    selfEvaluationDeadline: '',
    managerDeadline: '',
    hrReviewDeadline: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      year: currentYear,
      startDate: '',
      endDate: '',
      selfEvaluationDeadline: '',
      managerDeadline: '',
      hrReviewDeadline: '',
    });
    setEditingCycle(null);
    setIsFormOpen(false);
  };

  const handleEdit = (cycle: EvaluationCycleDTO) => {
    setEditingCycle(cycle);
    setFormData({
      name: cycle.name,
      year: cycle.year,
      startDate: cycle.startDate.split('T')[0] ?? '',
      endDate: cycle.endDate.split('T')[0] ?? '',
      selfEvaluationDeadline: cycle.selfEvaluationDeadline?.split('T')[0] ?? '',
      managerDeadline: cycle.managerDeadline?.split('T')[0] ?? '',
      hrReviewDeadline: cycle.hrReviewDeadline?.split('T')[0] ?? '',
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingCycle) {
      const updateData: UpdateEvaluationCycleRequest = {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        selfEvaluationDeadline: formData.selfEvaluationDeadline || undefined,
        managerDeadline: formData.managerDeadline || undefined,
        hrReviewDeadline: formData.hrReviewDeadline || undefined,
      };
      await updateCycle.mutateAsync({ id: editingCycle.id, data: updateData });
    } else {
      const createData: CreateEvaluationCycleRequest = {
        name: formData.name,
        year: formData.year,
        startDate: formData.startDate,
        endDate: formData.endDate,
        selfEvaluationDeadline: formData.selfEvaluationDeadline || undefined,
        managerDeadline: formData.managerDeadline || undefined,
        hrReviewDeadline: formData.hrReviewDeadline || undefined,
      };
      await createCycle.mutateAsync(createData);
    }
    resetForm();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Weet u zeker dat u deze beoordelingscyclus wilt verwijderen?')) {
      await deleteCycle.mutateAsync(id);
    }
  };

  const handleActivate = async (id: number) => {
    await activateCycle.mutateAsync(id);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van beoordelingscycli.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Beoordelingscycli</h2>
          <p className="text-sm text-gray-500">Beheer jaarlijkse beoordelingscycli en deadlines</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nieuwe Cyclus
        </button>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={resetForm}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingCycle ? 'Cyclus Bewerken' : 'Nieuwe Beoordelingscyclus'}
                </h3>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Naam
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Beoordeling 2024"
                      required
                    />
                  </div>

                  {/* Year (only for new) */}
                  {!editingCycle && (
                    <div>
                      <label htmlFor="year" className="block text-sm font-medium text-gray-700">
                        Jaar
                      </label>
                      <input
                        type="number"
                        id="year"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                        min={currentYear - 1}
                        max={currentYear + 5}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                  )}

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                        Startdatum
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                        Einddatum
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Deadlines */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Deadlines</h4>
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="selfEvaluationDeadline" className="block text-sm font-medium text-gray-700">
                          Zelfevaluatie deadline
                        </label>
                        <input
                          type="date"
                          id="selfEvaluationDeadline"
                          value={formData.selfEvaluationDeadline}
                          onChange={(e) => setFormData({ ...formData, selfEvaluationDeadline: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="managerDeadline" className="block text-sm font-medium text-gray-700">
                          Manager beoordeling deadline
                        </label>
                        <input
                          type="date"
                          id="managerDeadline"
                          value={formData.managerDeadline}
                          onChange={(e) => setFormData({ ...formData, managerDeadline: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="hrReviewDeadline" className="block text-sm font-medium text-gray-700">
                          HR review deadline
                        </label>
                        <input
                          type="date"
                          id="hrReviewDeadline"
                          value={formData.hrReviewDeadline}
                          onChange={(e) => setFormData({ ...formData, hrReviewDeadline: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={createCycle.isPending || updateCycle.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createCycle.isPending || updateCycle.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Cycles List */}
      {cycles && cycles.length > 0 ? (
        <div className="space-y-4">
          {cycles.map((cycle) => (
            <div
              key={cycle.id}
              className={`border rounded-lg p-6 ${
                cycle.isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-gray-900">{cycle.name}</h3>
                    {cycle.isActive && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                        Actief
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDate(cycle.startDate)} - {formatDate(cycle.endDate)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!cycle.isActive && (
                    <button
                      onClick={() => handleActivate(cycle.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50"
                      disabled={activateCycle.isPending}
                    >
                      Activeren
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(cycle)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    Bewerken
                  </button>
                  <button
                    onClick={() => handleDelete(cycle.id)}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                    disabled={deleteCycle.isPending}
                  >
                    Verwijderen
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                    Zelfevaluatie
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDate(cycle.selfEvaluationDeadline)}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                    Manager Beoordeling
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDate(cycle.managerDeadline)}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                    HR Review
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDate(cycle.hrReviewDeadline)}
                  </div>
                </div>
              </div>

              {cycle.evaluationsCount !== undefined && cycle.evaluationsCount > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-600">
                    {cycle.evaluationsCount} {cycle.evaluationsCount === 1 ? 'beoordeling' : 'beoordelingen'}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen beoordelingscycli gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">Begin met het toevoegen van een beoordelingscyclus.</p>
        </div>
      )}
    </div>
  );
}
